import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Clock, ArrowRight, ChevronLeft, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Project titles mapping for URL params
const projectTitles = {
  'sectorai-rag': 'SectorAI RAG sectoriel',
  'refonte-workflow-notices': 'workflow documentaire notices',
  'enrichissement-base-llm': 'enrichissement base données LLM',
  'assistant-teams': 'assistant IA Microsoft Teams',
};

// Category colors
const categoryColors = {
  'assistants': { gradient: ['#3dd3ee', '#61525a'], label: 'Assistants & Chatbots' },
  'documents': { gradient: ['#fad24b', '#ff8c19'], label: 'Traitement de documents' },
  'generation': { gradient: ['#b4dc19', '#3dd3ee'], label: 'Génération de contenu' },
  'vision': { gradient: ['#c8aff0', '#3dd3ee'], label: 'Vision & OCR' },
  'ia-custom': { gradient: ['#ff8c19', '#fad24b'], label: 'IA sur-mesure' },
  'data': { gradient: ['#61525a', '#b4dc19'], label: 'Data & Intégration' },
  'case-study': { gradient: ['#3dd3ee', '#c8aff0'], label: 'Étude de cas' },
};

const ProjectCard = ({ project, isLarge = false }) => {
  const colors = categoryColors[project.category] || categoryColors['assistants'];
  const [color1, color2] = colors.gradient;

  return (
    <div
      className={`
        relative rounded-2xl overflow-hidden
        backdrop-blur-xl border border-white/10
        transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl
        ${isLarge ? 'p-8' : 'p-6'}
      `}
      style={{
        background: `linear-gradient(135deg, ${color1}15, ${color2}10, rgba(30,25,25,0.95))`,
        boxShadow: `0 4px 30px ${color1}10`,
      }}
    >
      {/* Gradient accent */}
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: `linear-gradient(90deg, ${color1}, ${color2})` }}
      />
      
      {/* Category badge */}
      <div 
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4"
        style={{ 
          background: `linear-gradient(135deg, ${color1}30, ${color2}20)`,
          color: color1
        }}
      >
        <div 
          className="w-2 h-2 rounded-full"
          style={{ background: `linear-gradient(135deg, ${color1}, ${color2})` }}
        />
        {colors.label}
      </div>

      <h3 className={`font-bold text-white mb-3 ${isLarge ? 'text-2xl' : 'text-lg'}`}>
        {project.title}
      </h3>

      <div className="mb-4">
        <p className="text-[#3dd3ee] font-medium text-sm mb-2">Objectif</p>
        <p className="text-[#bbb5ae] text-sm leading-relaxed">
          {project.objectif}
        </p>
      </div>

      {isLarge && (
        <div className="mb-4">
          <p className="text-[#fad24b] font-medium text-sm mb-2">Description</p>
          <p className="text-[#bbb5ae] text-sm leading-relaxed">
            {project.description}
          </p>
        </div>
      )}

      {project.resultats && isLarge && (
        <div className="mb-4">
          <p className="text-[#b4dc19] font-medium text-sm mb-2">Résultats</p>
          <p className="text-[#bbb5ae] text-sm leading-relaxed">
            {project.resultats}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center gap-2 text-[#736c64]">
          <Clock className="w-4 h-4" />
          <span className="text-sm">{project.duree}</span>
        </div>
        {project.similarity_score && (
          <div 
            className="text-xs font-medium px-2 py-1 rounded-full"
            style={{ background: `${color1}20`, color: color1 }}
          >
            {Math.round(project.similarity_score * 100)}% pertinent
          </div>
        )}
      </div>
    </div>
  );
};

const Carousel = ({ projects }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);
  
  // Show 3 items at a time on desktop
  const itemsPerView = 3;
  const totalSlides = Math.ceil(projects.length / itemsPerView);

  useEffect(() => {
    if (isAutoPlaying && projects.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % totalSlides);
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isAutoPlaying, projects.length, totalSlides]);

  const goTo = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goNext = () => goTo((currentIndex + 1) % totalSlides);
  const goPrev = () => goTo((currentIndex - 1 + totalSlides) % totalSlides);

  if (projects.length === 0) return null;

  return (
    <div className="relative">
      {/* Carousel container */}
      <div className="overflow-hidden rounded-2xl">
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {/* Group projects into slides of 3 */}
          {Array.from({ length: totalSlides }).map((_, slideIndex) => (
            <div key={slideIndex} className="w-full flex-shrink-0 px-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {projects.slice(slideIndex * itemsPerView, (slideIndex + 1) * itemsPerView).map((project) => (
                  <ProjectCard key={project.id} project={project} isLarge={false} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows - only show if more than one slide */}
      {totalSlides > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-[#1e1919]/80 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-[#1e1919] transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-[#1e1919]/80 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-[#1e1919] transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dots indicator - only show if more than one slide */}
      {totalSlides > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'w-8 bg-gradient-to-r from-[#3dd3ee] to-[#c8aff0]' 
                  : 'w-2 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [carouselProjects, setCarouselProjects] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const initialSearchDone = useRef(false);

  // Function to navigate to contact section
  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  // Handle URL param for project search
  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam && !initialSearchDone.current) {
      initialSearchDone.current = true;
      const searchTerm = projectTitles[projectParam] || projectParam;
      setSearchQuery(searchTerm);
      // Trigger search after a short delay
      setTimeout(() => {
        performSearch(searchTerm, true);
      }, 500);
    }
  }, [searchParams]);

  // Load carousel projects on mount
  useEffect(() => {
    const loadCarouselProjects = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/projects/carousel`);
        if (response.ok) {
          const data = await response.json();
          setCarouselProjects(data);
        }
      } catch (error) {
        console.error('Error loading carousel projects:', error);
      }
    };
    loadCarouselProjects();
  }, []);

  // Perform search function (reusable)
  const performSearch = async (query, scrollToResults = false) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      } else {
        console.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
      // Scroll to results if requested
      if (scrollToResults) {
        setTimeout(() => {
          const resultsSection = document.querySelector('#search-results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section with Search */}
        <section className="relative py-24 overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#3dd3ee]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#c8aff0]/10 rounded-full blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:60px_60px]" />
          </div>

          <div className="relative max-w-4xl mx-auto px-6">
            {/* Title */}
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-sm font-medium text-[#bbb5ae] mb-6">
                <Sparkles className="w-4 h-4 text-[#fad24b]" />
                +50 projets livrés en production
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                Nos <span className="text-[#3dd3ee]">Réalisations</span>
              </h1>
              <p className="text-lg text-[#bbb5ae]">
                Trouvez votre projet dans nos réalisations
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative">
              <div className="relative group">
                <div 
                  className="absolute -inset-1 rounded-2xl opacity-50 group-hover:opacity-75 blur-xl transition-all duration-500"
                  style={{ background: 'linear-gradient(135deg, #3dd3ee40, #c8aff040)' }}
                />
                <div className="relative flex items-center bg-[#1e1919]/80 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-center w-14 h-14 text-[#736c64]">
                    <Search className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Décrivez votre besoin... (ex: automatiser le traitement des factures)"
                    className="flex-1 h-14 bg-transparent text-white placeholder-[#736c64] outline-none pr-4"
                  />
                  <Button
                    type="submit"
                    disabled={isSearching || !searchQuery.trim()}
                    className="m-2 bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] font-semibold px-6 py-2 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(61,211,238,0.4)] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSearching ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Rechercher
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </section>

        {/* Search Result */}
        {hasSearched && (
          <section id="search-results" className="relative py-12 px-6">
            <div className="max-w-6xl mx-auto">
              {/* Message d'aide */}
              <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-[#3dd3ee]/10 to-[#c8aff0]/10 border border-white/10 backdrop-blur-sm">
                <p className="text-[#bbb5ae] text-center">
                  Ça ne correspond pas parfaitement à votre besoin ?{' '}
                  <button
                    onClick={goToContact}
                    className="text-[#3dd3ee] hover:text-[#fad24b] transition-colors font-medium underline underline-offset-4"
                  >
                    Contactez-nous
                  </button>
                  , nous pouvons probablement vous aider.
                </p>
              </div>

              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ background: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}
                />
                Top 3 des projets les plus pertinents
              </h2>
              
              {isSearching ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-[#3dd3ee]" />
                  <span className="ml-3 text-[#bbb5ae]">Recherche en cours...</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {searchResults.map((project, index) => (
                    <div key={project.id} className="relative">
                      {/* Rank indicator */}
                      <div 
                        className="absolute -left-2 -top-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10"
                        style={{ 
                          background: index === 0 
                            ? 'linear-gradient(135deg, #fad24b, #ff8c19)' 
                            : index === 1 
                              ? 'linear-gradient(135deg, #c8aff0, #3dd3ee)'
                              : 'linear-gradient(135deg, #61525a, #736c64)',
                          color: '#0a0a0b'
                        }}
                      >
                        {index + 1}
                      </div>
                      <ProjectCard project={project} isLarge={false} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#736c64]">
                  Aucun résultat trouvé. Essayez une autre recherche.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Descriptive Section */}
        <section className="relative py-24 px-6 bg-[#1e1919]/50">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
          
          <div className="relative max-w-4xl mx-auto">
            <div className="space-y-6 text-[#bbb5ae] leading-relaxed">
              <p>
                Nous concevons des <span className="text-white font-medium">solutions IA sur mesure</span>, intégrées aux outils existants et conçues pour des <span className="text-white font-medium">usages métiers réels</span>.
              </p>
              <p>
                Notre approche repose sur la <span className="text-[#3dd3ee]">donnée propriétaire</span>, des <span className="text-[#c8aff0]">LLM gouvernés</span> et une <span className="text-[#fad24b]">intégration opérationnelle</span> dans les processus des équipes.
              </p>
              <p>
                De la création de <span className="text-white font-medium">RAG sectoriels à grande échelle</span> à l'automatisation de workflows complexes, nos projets génèrent des <span className="text-[#b4dc19]">résultats mesurables</span> : productivité accrue, adoption forte et renouvellement d'offres data.
              </p>
              <p>
                Nous accompagnons nos clients <span className="text-white font-medium">de la structuration de la donnée à l'industrialisation de l'IA</span>, avec un objectif clair : des solutions utiles, fiables et réellement utilisées.
              </p>
            </div>
          </div>
        </section>

        {/* Carousel Section */}
        <section className="relative py-24 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                Études de cas <span className="text-[#c8aff0]">sélectionnées</span>
              </h2>
              <p className="text-[#bbb5ae]">
                Découvrez nos projets phares et leurs résultats concrets
              </p>
            </div>
            
            <Carousel projects={carouselProjects} />
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Vous avez un projet similaire ?
            </h2>
            <p className="text-[#bbb5ae] mb-8">
              Discutons de votre besoin et voyons comment nous pouvons vous accompagner.
            </p>
            <Button
              onClick={goToContact}
              className="relative bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] font-semibold px-8 py-3 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(61,211,238,0.4)] overflow-hidden rounded-lg group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
              <span className="relative z-10 flex items-center gap-2">
                Contactez-nous
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
